import { useState, useEffect, useMemo } from 'react';
import { createClient } from '../supabase/client';

export interface ListDisplayName {
  list_key: string;
  display_name: string;
  emoji: string | null;
  list_type: string;
  is_premium: boolean;
  sort_order: number;
}

// Кэш для хранения данных
let globalCache: Map<string, ListDisplayName> | null = null;
let isLoading = false;
let loadPromise: Promise<void> | null = null;
let cacheVersionAtLoad: string | null = null; // Версия кеша, которая была при загрузке данных

// Версионирование кэша для принудительного обновления при изменении данных
const CACHE_VERSION = '1.0.6'; // US Mid Caps emoji: usmidcaps → 🇺🇸 (cache bust)

/** Source-of-truth overrides when DB/localStorage lag behind admin updates. */
const LIST_EMOJI_OVERRIDES: Record<string, string> = {
  usmidcaps: '🇺🇸',
};
const CACHE_VERSION_KEY = 'list-display-names-cache-version';
const CACHE_DATA_KEY = 'list-display-names-data';

function restoreCacheFromLocalStorage(): Map<string, ListDisplayName> | null {
  if (typeof window === 'undefined') return null;
  try {
    const cachedVersion = localStorage.getItem(CACHE_VERSION_KEY);
    if (cachedVersion !== CACHE_VERSION) return null;
    const raw = localStorage.getItem(CACHE_DATA_KEY);
    if (!raw) return null;
    const entries: [string, ListDisplayName][] = JSON.parse(raw);
    return new Map(entries);
  } catch {
    return null;
  }
}

function saveCacheToLocalStorage(cache: Map<string, ListDisplayName>): void {
  if (typeof window === 'undefined') return;
  try {
    const entries = Array.from(cache.entries());
    localStorage.setItem(CACHE_DATA_KEY, JSON.stringify(entries));
  } catch {
    // localStorage quota exceeded — ignore
  }
}

// Попытка восстановить кеш из localStorage при инициализации модуля
if (typeof window !== 'undefined' && !globalCache) {
  const restored = restoreCacheFromLocalStorage();
  if (restored && restored.size > 0) {
    globalCache = restored;
    cacheVersionAtLoad = CACHE_VERSION;
  }
}

// Утилита для нормализации названий (как в оригинальном коде)
// Должна быть определена до loadDisplayNames, так как используется там
export function normalizeIndustry(s: string): string {
  return s.replace(/\s*&\s*/g, "&").replace(/\s+/g, " ").trim();
}

async function loadDisplayNames(): Promise<Map<string, ListDisplayName>> {
  // Проверяем версию кэша в localStorage ПЕРЕД проверкой кеша в памяти
  if (typeof window !== 'undefined') {
    const cachedVersion = localStorage.getItem(CACHE_VERSION_KEY);
    
    // Если версия в localStorage не совпадает с текущей версией - очищаем кеш
    if (cachedVersion !== CACHE_VERSION) {
      globalCache = null;
      loadPromise = null;
      cacheVersionAtLoad = null;
      localStorage.removeItem(CACHE_DATA_KEY);
      if (cachedVersion) {
        console.log('Cache version changed, clearing cache. Old version:', cachedVersion, 'New version:', CACHE_VERSION);
      }
    }
    
    // Если кеш в памяти есть, но версия при загрузке не совпадает с текущей - очищаем
    if (globalCache && cacheVersionAtLoad !== CACHE_VERSION) {
      console.log('Cache version mismatch detected, clearing cache. Cache version:', cacheVersionAtLoad, 'Current version:', CACHE_VERSION);
      globalCache = null;
      loadPromise = null;
      cacheVersionAtLoad = null;
    }
  }

  // Если уже загружается, ждем завершения
  if (loadPromise) {
    await loadPromise;
    return globalCache || new Map();
  }

  // Если уже загружено и версия совпадает, возвращаем кэш
  if (globalCache && cacheVersionAtLoad === CACHE_VERSION) {
    return globalCache;
  }

  // Начинаем загрузку
  isLoading = true;
  loadPromise = (async () => {
    try {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('list_display_names')
        .select('list_key, display_name, emoji, list_type, is_premium, sort_order');

      if (error) {
        console.error('Error loading list display names:', error);
        globalCache = new Map();
        cacheVersionAtLoad = null;
        return;
      }

      // Создаем Map для быстрого поиска: ключ = `${list_type}:${list_key}`
      const cache = new Map<string, ListDisplayName>();
      (data || []).forEach((item) => {
        const key = `${item.list_type}:${item.list_key}`;
        cache.set(key, item);
        // Также добавляем без типа для обратной совместимости
        cache.set(item.list_key, item);
        
        // Для индустрий: добавляем нормализованные ключи для поиска
        if (item.list_type === 'industry' || item.list_type === 'industrygroup') {
          const normalized = normalizeIndustry(item.list_key);
          if (normalized !== item.list_key) {
            // Добавляем нормализованный ключ с типом
            const normalizedKey = `${item.list_type}:${normalized}`;
            cache.set(normalizedKey, item);
            // И без типа
            cache.set(normalized, item);
          }
        }
      });

      globalCache = cache;
      cacheVersionAtLoad = CACHE_VERSION;
      
      // Сохраняем версию и данные кэша в localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem(CACHE_VERSION_KEY, CACHE_VERSION);
        saveCacheToLocalStorage(cache);
      }
    } catch (err) {
      console.error('Error loading list display names:', err);
      globalCache = new Map();
      cacheVersionAtLoad = null;
    } finally {
      isLoading = false;
      loadPromise = null;
    }
  })();

  await loadPromise;
  return globalCache || new Map();
}

/** After the first client mount we can reuse the in-memory cache immediately.
 *  During SSR + the hydration render it must stay empty, or localStorage-restored
 *  emojis make the tree differ from the server HTML. */
let clientReady = false;

export function useListDisplayNames() {
  const [cache, setCache] = useState<Map<string, ListDisplayName>>(() => (
    clientReady && globalCache ? globalCache : new Map()
  ));
  const [isLoading, setIsLoading] = useState(!(clientReady && globalCache));

  useEffect(() => {
    clientReady = true;
    // Проверяем версию кеша при монтировании компонента
    if (typeof window !== 'undefined') {
      const cachedVersion = localStorage.getItem(CACHE_VERSION_KEY);
      if (cachedVersion !== CACHE_VERSION) {
        // Версия изменилась - очищаем кеш и перезагружаем
        globalCache = null;
        loadPromise = null;
        cacheVersionAtLoad = null;
      }
    }

    // Проверяем, что кеш в памяти актуален (версия совпадает)
    if (globalCache && cacheVersionAtLoad === CACHE_VERSION) {
      setCache(globalCache);
      setIsLoading(false);
      return;
    }
    
    // Если кеш есть, но версия не совпадает - очищаем
    if (globalCache && cacheVersionAtLoad !== CACHE_VERSION) {
      globalCache = null;
      loadPromise = null;
      cacheVersionAtLoad = null;
    }

    setIsLoading(true);
    loadDisplayNames().then((loadedCache) => {
      setCache(loadedCache);
      setIsLoading(false);
    });
  }, []);

  const getEmoji = useMemo(() => {
    return (listKey: string, listType?: string): string => {
      if (!listKey) return '';

      const override = LIST_EMOJI_OVERRIDES[listKey];
      if (override) return override;
      
      // Пробуем найти с типом
      if (listType) {
        const key = `${listType}:${listKey}`;
        const item = cache.get(key);
        if (item?.emoji) return item.emoji;
      }
      
      // Пробуем найти без типа (для обратной совместимости)
      const item = cache.get(listKey);
      if (item?.emoji) return item.emoji;
      
      return '';
    };
  }, [cache]);

  const getDisplayName = useMemo(() => {
    return (listKey: string, listType?: string): string => {
      if (!listKey) return '';
      
      // Пробуем найти с типом
      if (listType) {
        const key = `${listType}:${listKey}`;
        const item = cache.get(key);
        if (item?.display_name) return item.display_name;
      }
      
      // Пробуем найти без типа
      const item = cache.get(listKey);
      if (item?.display_name) return item.display_name;
      
      return listKey;
    };
  }, [cache]);

  return {
    getEmoji,
    getDisplayName,
    isLoading,
    cache,
  };
}

// Функции для прямого доступа к кэшу (без хуков) - для использования в классах
export function getEmojiFromCache(listKey: string, listType?: string): string {
  if (!listKey || !globalCache) return '';

  const override = LIST_EMOJI_OVERRIDES[listKey];
  if (override) return override;
  
  if (listType) {
    const key = `${listType}:${listKey}`;
    const item = globalCache.get(key);
    if (item?.emoji) return item.emoji;
  }
  
  const item = globalCache.get(listKey);
  if (item?.emoji) return item.emoji;
  
  return '';
}

export function getCountryEmojiFromCache(countryName?: string | null): string {
  if (!countryName || !globalCache) return '';
  return getEmojiFromCache(countryName.trim(), 'country');
}

export function getIndustryEmojiFromCache(industryName?: string | null, industryType: 'industry' | 'industrygroup' = 'industrygroup'): string {
  if (!industryName || !globalCache) return '';
  const normalized = normalizeIndustry(industryName);
  const original = industryName.trim();
  
  // Пробуем сначала нормализованное значение с указанным типом
  let emoji = getEmojiFromCache(normalized, industryType);
  if (emoji) return emoji;
  
  // Пробуем оригинальное значение с указанным типом (на случай если в БД уже нормализовано)
  emoji = getEmojiFromCache(original, industryType);
  if (emoji) return emoji;
  
  // Пробуем нормализованное значение с другим типом
  emoji = getEmojiFromCache(normalized, industryType === 'industrygroup' ? 'industry' : 'industrygroup');
  if (emoji) return emoji;
  
  // Пробуем оригинальное значение с другим типом
  return getEmojiFromCache(original, industryType === 'industrygroup' ? 'industry' : 'industrygroup');
}

// Инициализация кэша при импорте (для использования в классах)
if (typeof window !== 'undefined') {
  // Проверяем версию кеша при инициализации
  const cachedVersion = localStorage.getItem(CACHE_VERSION_KEY);
  if (cachedVersion !== CACHE_VERSION) {
    // Версия изменилась - очищаем кеш
    globalCache = null;
    loadPromise = null;
    cacheVersionAtLoad = null;
    if (cachedVersion) {
      console.log('Cache version changed, clearing cache. Old version:', cachedVersion, 'New version:', CACHE_VERSION);
    }
  } else if (globalCache && cacheVersionAtLoad !== CACHE_VERSION) {
    // Если кеш в памяти есть, но версия не совпадает - очищаем
    globalCache = null;
    loadPromise = null;
    cacheVersionAtLoad = null;
  }
  loadDisplayNames().catch(console.error);
}

// Функция для принудительной очистки кэша (можно вызвать из консоли браузера)
export function clearListDisplayNamesCache() {
  globalCache = null;
  loadPromise = null;
  cacheVersionAtLoad = null;
  if (typeof window !== 'undefined') {
    localStorage.removeItem(CACHE_VERSION_KEY);
    localStorage.removeItem(CACHE_DATA_KEY);
  }
  console.log('List display names cache cleared. Reload the page to fetch fresh data.');
}

// Функция для получения emoji страны (использует БД)
export function useCountryEmoji() {
  const { getEmoji } = useListDisplayNames();
  
  return useMemo(() => {
    return (countryName?: string | null): string => {
      if (!countryName) return '';
      return getEmoji(countryName.trim(), 'country');
    };
  }, [getEmoji]);
}

// Функция для получения emoji индустрии (использует БД)
export function useIndustryEmoji() {
  const { getEmoji } = useListDisplayNames();
  
  return useMemo(() => {
    return (industryName?: string | null, industryType: 'industry' | 'industrygroup' = 'industrygroup'): string => {
      if (!industryName) return '';
      const normalized = normalizeIndustry(industryName);
      const original = industryName.trim();
      
      // Пробуем сначала нормализованное значение с указанным типом
      let emoji = getEmoji(normalized, industryType);
      if (emoji) return emoji;
      
      // Пробуем оригинальное значение с указанным типом
      emoji = getEmoji(original, industryType);
      if (emoji) return emoji;
      
      // Пробуем нормализованное значение с другим типом
      emoji = getEmoji(normalized, industryType === 'industrygroup' ? 'industry' : 'industrygroup');
      if (emoji) return emoji;
      
      // Пробуем оригинальное значение с другим типом
      return getEmoji(original, industryType === 'industrygroup' ? 'industry' : 'industrygroup');
    };
  }, [getEmoji]);
}

